import type { ReactElement } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { DeleteJobStatus } from './api';
import { isTerminalDeleteJob } from './api';

/** `'pending'` = the launch request is in flight (no job id yet); a `DeleteJobStatus` = a live job. */
export type DeletePhase = 'pending' | DeleteJobStatus | null;

interface DeleteStatusBadgeProps {
  readonly phase: DeletePhase;
}

/**
 * Inline in-flight / failure indicator for a running delete job — the `JobProgressIndicator`-style
 * affordance each delete surface (tile, family header, patient header) renders while a delete is
 * launching or executing. Shows a spinner + "Deleting…" while non-terminal, and a red failure line if
 * the job ends FAILED/STOPPED/ABANDONED (a COMPLETED job resolves by the card disappearing, so it
 * renders nothing).
 */
export function DeleteStatusBadge({ phase }: DeleteStatusBadgeProps): ReactElement | null {
  const { t } = useTranslation();

  if (phase === null) return null;

  const running = phase === 'pending' || !isTerminalDeleteJob(phase);
  if (running) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        {t('delete.status.deleting', { defaultValue: 'Deleting…' })}
      </span>
    );
  }

  if (phase === 'COMPLETED') return null;

  return (
    <span role="alert" className="inline-flex items-center gap-1.5 text-[12px] text-destructive">
      <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
      {t('delete.status.failed', { defaultValue: 'Delete failed' })}
    </span>
  );
}
